import React, { useEffect, useState } from 'react';

import { useLocation, useSearchParams } from 'react-router-dom';

import { Box, Tab, Tabs } from '@mui/material';

import ClassicRewardsReservationsPage from '../ClassicRewardsReservationsPage';
import FlashRewardsPage from '../FlashRewards/FlashRewardsPage';
import LoyaltyPayForPointsPage from '../PayForPoints';
import SocieteEsimGeneratorPage from '../SocieteEsimGeneratorPage';
import { loyaltySubTabsContainerSx } from '../loyaltyTabStyles';

type StatsSubTab = 'esim' | 'pay-for-points' | 'classic-rewards' | 'flash-rewards';

const SUB_TABS: ReadonlyArray<StatsSubTab> = ['esim', 'pay-for-points', 'classic-rewards', 'flash-rewards'];
const DEFAULT_SUB_TAB: StatsSubTab = 'esim';

function isStatsSubTab(value: string | null): value is StatsSubTab {
  return value !== null && (SUB_TABS as ReadonlyArray<string>).includes(value);
}

/**
 * Offers page: combines the eSIM generator, Pay-for-Points report, Charter Flight Rewards
 * (classic rewards) reservations and Flash Hotel Rewards (Société flash-hotel rewards) under a
 * single top-level loyalty tab.
 *
 * Honours `?tab=<sub-tab>` so deep links (e.g. /loyalty/societe/offers?tab=classic-rewards)
 * land on the correct sub-tab. The sub-tab slugs keep the legacy names so old links still work.
 */
export default function StatsPage() {
  const location = useLocation();
  const initialTab = (() => {
    const param = new URLSearchParams(location.search).get('tab');
    return isStatsSubTab(param) ? param : DEFAULT_SUB_TAB;
  })();
  const [tab, setTab] = useState<StatsSubTab>(initialTab);
  const [, setSearchParams] = useSearchParams();

  // Keep the sub-tab in the URL so a refresh or shared link lands on it. Switching sub-tabs drops
  // the previous one's own params (e.g. Flash Rewards' flashTab / offerId).
  const handleTabChange = (_event: React.SyntheticEvent, next: StatsSubTab) => {
    setTab(next);
    setSearchParams({ tab: next }, { replace: true });
  };

  // Update if the URL query string changes (e.g. via a sibling link).
  useEffect(() => {
    const param = new URLSearchParams(location.search).get('tab');
    if (isStatsSubTab(param)) {
      setTab(param);
    }
  }, [location.search]);

  return (
    <Box>
      <Box sx={loyaltySubTabsContainerSx}>
        <Tabs value={tab} onChange={handleTabChange}>
          <Tab value="esim" label="eSIM" />
          <Tab value="pay-for-points" label="Pay for Points" />
          <Tab value="classic-rewards" label="Charter Flight Rewards" />
          <Tab value="flash-rewards" label="Flash Hotel Rewards" />
        </Tabs>
      </Box>

      {tab === 'esim' && <SocieteEsimGeneratorPage />}
      {tab === 'pay-for-points' && <LoyaltyPayForPointsPage />}
      {tab === 'classic-rewards' && <ClassicRewardsReservationsPage />}
      {tab === 'flash-rewards' && <FlashRewardsPage />}
    </Box>
  );
}
